import type { LatLng } from '@agos/shared'

/**
 * Where a delivery goes, held beside the address text - FRONTEND.md §9.706, FB-1010, owner O81.
 *
 * **The checkout kept the last point whatever was typed after it.** A picked suggestion set `point`, typing called
 * `setAddress` and nothing else, so a customer who picked *Hlavná 1, Šurany* and then typed *Štúrova 5, Nitra* was
 * quoted Šurany's zone and sent Šurany's coordinates beside the Nitra text - and the server prices the zone and the
 * minimum from those coordinates, and the driver's *Navigovať* goes to them (§9.504). Legacy cleared the point on
 * every keystroke (`Checkout.tsx:1919`). O81 puts the point on a map, where a stale one would be drawn as a pin.
 *
 * So the screen holds a point **and what it belongs to**, and asks `pointBehind` on every render rather than erasing
 * the point in a handler - which is also why typing an address back restores its point instead of losing it:
 *
 *   1. **found for a text** - a picked suggestion, a chosen saved address, the profile's default address - it is the
 *      order's point **while the address still begins with that text**. Appending to a picked address (a flat, a
 *      floor, a bell) does not move the building; changing the street, the number or the town does. AGOS does not
 *      geocode typed text (§9.371), so exact equality - the manager's rule, §9.693 - would cost the customer their
 *      quote for typing *, byt 5*.
 *   2. **given as a place** - a pin put down on the map, or *Moja poloha* - it is the place and the text is its label,
 *      so correcting the label keeps it. Under rule 1 a drop would loop: the drop names the place *Hlavná 3*, the
 *      customer corrects it to *Hlavná 3A*, the pin would go, and dropping it again renames the box again.
 */
export interface HeldPoint {
  point: LatLng
  /** The address text the point was found for, or `null` when the point came first and the text is its label. */
  foundFor: string | null
}

/** A point a geocoder found for this text. */
export function foundForAddress(point: LatLng, address: string): HeldPoint {
  return { point, foundFor: address.trim() }
}

/** A point the customer gave themselves - a pin on the map, or their own location. */
export function givenAsAPlace(point: LatLng): HeldPoint {
  return { point, foundFor: null }
}

/**
 * The point the order carries for this address, or `null` - priced, sent and drawn only when this answers one.
 *
 * Returns the held point's own object, so a screen that puts the answer in an effect's dependencies does not see a
 * new point on every render.
 */
export function pointBehind(held: HeldPoint | null, address: string): LatLng | null {
  if (held === null) return null
  if (held.foundFor === null) return held.point
  // A point found for nothing describes nothing - and every text begins with the empty string.
  if (held.foundFor === '') return null
  return address.trim().startsWith(held.foundFor) ? held.point : null
}

/**
 * What survives choosing another address - a saved one, before its own lookup answers.
 *
 * **A place goes**: it would survive the new text by rule 2, and choosing an address is choosing a place, so a
 * failed lookup must not leave a pin behind somebody else's address. A point found for a text stays held, and is
 * carried again only if the customer comes back to that text.
 */
export function heldWhenAnAddressIsChosen(held: HeldPoint | null): HeldPoint | null {
  return held !== null && held.foundFor === null ? null : held
}
